/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * AuthLayout Component
 * ============================================================================
 * 
 * Glassmorphic authentication layout with animated glow border.
 * Used for Login, MFA, and Password Reset screens.
 * 
 * UPDATED: Matches Phase 1 prototype exactly with:
 *   - Dual animation system (glowMove + pulseGlow)
 *   - ::before/::after pseudo-element glow border
 *   - Static positioned background glows (no floating)
 *   - Correct glassmorphism values (0.7 opacity, 24px blur)
 * 
 * ============================================================================
 */

import React, { useEffect } from 'react';
import { useTheme } from '@/lib/theme';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { LanguageToggle } from '@/components/ui/LanguageToggle';
import { projectConfig, getLogo } from '@/config/project.config';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';

interface AuthLayoutProps {
  children: React.ReactNode;
  /** Title shown above the form */
  title?: string;
  /** Subtitle/description */
  subtitle?: string;
  /** Icon to display above title */
  icon?: React.ReactNode;
  /** Icon color variant */
  iconVariant?: 'red' | 'gold' | 'teal' | 'default';
  /** Whether to show the logo */
  showLogo?: boolean;
  /** Maximum width of the card */
  maxWidth?: 'sm' | 'md' | 'lg';
}

export function AuthLayout({
  children,
  title,
  subtitle,
  icon,
  iconVariant = 'default',
  showLogo = true,
  maxWidth = 'sm',
}: AuthLayoutProps) {
  const { t } = useTranslation('auth');
  const { isDark } = useTheme();

  // Preload auth namespace for all auth pages
  useEffect(() => { i18n.loadNamespaces('auth'); }, []);

  const maxWidthStyles = {
    sm: '420px',  // Matches mockup exactly
    md: '480px',
    lg: '540px',
  };

  return (
    <div className="auth-layout">
      {/* Background with radial gradient */}
      <div className="auth-background">
        {/* Static positioned glow effects - matching prototype exactly */}
        <div className="auth-bg-glow-top" />
        <div className="auth-bg-glow-bottom" />
      </div>

      {/* Theme & Language toggles - top right */}
      <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
        <LanguageToggle size="md" />
        <ThemeToggle showSystemOption size="md" />
      </div>

      {/* Main content */}
      <div className="auth-container">
        {/* Card with animated glow border - uses glow-border class */}
        <div className="auth-card glow-border" style={{ maxWidth: maxWidthStyles[maxWidth] }}>
          {/* Card content with glassmorphism */}
          <div className="auth-card-content glass-card">
            {/* Logo */}
            {showLogo && (
              <div className="auth-logo">
                <div className="auth-logo-circle">
                  <img
                    src={getLogo(isDark)}
                    alt={projectConfig.name}
                  />
                </div>
              </div>
            )}

            {/* Icon */}
            {icon && (
              <div className={`auth-icon auth-icon-${iconVariant}`}>
                {icon}
              </div>
            )}

            {/* Title & Subtitle */}
            {(title || subtitle) && (
              <div className="auth-header">
                {title && (
                  <h1 className="auth-title">{title}</h1>
                )}
                {subtitle && (
                  <p className="auth-subtitle">{subtitle}</p>
                )}
              </div>
            )}

            {/* Form content */}
            <div className="auth-form">
              {children}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="auth-footer">
          <p>{projectConfig.legal.copyright}</p>
          <span className="mx-2">•</span>
          <p>{t('authLayout.enterprisePlatform')}</p>
          <span className="auth-footer-sep">•</span>
          <a href={projectConfig.legal.links.privacy} className="auth-footer-legal-link">
            {t('authLayout.privacy', 'Confidentialité')}
          </a>
          <span className="auth-footer-dot">·</span>
          <a href={projectConfig.legal.links.terms} className="auth-footer-legal-link">
            {t('authLayout.terms', 'Conditions')}
          </a>
        </div>
      </div>

      {/* Styles - Matching Phase 1 Prototype Exactly */}
      <style>{`
        /* ═══════════════════════════════════════════════════════════════════════════
         *  ANIMATED GLOW BORDER (From Phase 1 Prototype)
         * ═══════════════════════════════════════════════════════════════════════════ */
        
        @keyframes glowMove {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes pulseGlow {
          0%, 100% { opacity: 0.5; filter: blur(15px); }
          50% { opacity: 0.8; filter: blur(20px); }
        }
        
        .glow-border {
          position: relative;
        }
        
        .glow-border::before {
          content: '';
          position: absolute;
          inset: -2px;
          border-radius: inherit;
          padding: 2px;
          background: linear-gradient(90deg, transparent 0%, #8B0000 25%, #A31621 50%, #8B0000 75%, transparent 100%);
          background-size: 300% 100%;
          animation: glowMove 4s ease-in-out infinite;
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          pointer-events: none;
        }
        
        .glow-border::after {
          content: '';
          position: absolute;
          inset: -10px;
          border-radius: inherit;
          background: linear-gradient(90deg, transparent 0%, rgba(139, 0, 0, 0.3) 25%, rgba(163, 22, 33, 0.4) 50%, rgba(139, 0, 0, 0.3) 75%, transparent 100%);
          background-size: 300% 100%;
          animation: glowMove 4s ease-in-out infinite, pulseGlow 2s ease-in-out infinite;
          filter: blur(15px);
          z-index: -1;
          pointer-events: none;
        }
        
        @media (prefers-reduced-motion: reduce) {
          .glow-border::before, .glow-border::after {
            animation: none;
            background-position: 50% 50%;
          }
        }
        
        /* Light theme - animated glow with teal colors */
        [data-theme="light"] .glow-border::before {
          background: linear-gradient(90deg, transparent 0%, #1A5F7A 25%, #3D96BD 50%, #1A5F7A 75%, transparent 100%);
          background-size: 300% 100%;
          animation: glowMove 4s ease-in-out infinite;
        }

        [data-theme="light"] .glow-border::after {
          background: linear-gradient(90deg, transparent 0%, rgba(26, 95, 122, 0.3) 25%, rgba(61, 150, 189, 0.4) 50%, rgba(26, 95, 122, 0.3) 75%, transparent 100%);
          background-size: 300% 100%;
          animation: glowMove 4s ease-in-out infinite, pulseGlow 2s ease-in-out infinite;
        }
        
        /* ═══════════════════════════════════════════════════════════════════════════
         *  GLASSMORPHISM (From Phase 1 Prototype)
         * ═══════════════════════════════════════════════════════════════════════════ */
        
        .glass-card {
          background: rgba(26, 29, 41, 0.7);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 16px;
        }
        
        [data-theme="light"] .glass-card {
          background: rgba(255, 255, 255, 0.9);
          border: 1px solid rgba(0, 0, 0, 0.08);
        }

        /* ═══════════════════════════════════════════════════════════════════════════
         *  AUTH LAYOUT (Matching Phase 1 Login Page)
         * ═══════════════════════════════════════════════════════════════════════════ */

        .auth-layout {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
        }

        /* Background with radial gradient - matching prototype */
        .auth-background {
          position: fixed;
          inset: 0;
          background: radial-gradient(ellipse at 50% 0%, rgba(26, 29, 41, 1) 0%, rgba(13, 15, 20, 1) 100%);
          z-index: -2;
        }
        
        [data-theme="light"] .auth-background {
          background: linear-gradient(135deg, #F7FAFC 0%, #E2E8F0 100%);
        }

        /* Static positioned glow effects - matching mockup exactly */
        .auth-bg-glow-top {
          position: absolute;
          top: -25%;
          left: 50%;
          transform: translateX(-50%);
          width: 800px;
          height: 400px;
          background: radial-gradient(ellipse, rgba(139, 0, 0, 0.4) 0%, transparent 70%);
          filter: blur(60px);
          opacity: 0.2;
          pointer-events: none;
        }

        .auth-bg-glow-bottom {
          position: absolute;
          bottom: -25%;
          left: 50%;
          transform: translateX(-50%);
          width: 600px;
          height: 300px;
          background: radial-gradient(ellipse, rgba(26, 95, 122, 0.5) 0%, transparent 70%);
          filter: blur(80px);
          opacity: 0.1;
          pointer-events: none;
        }

        [data-theme="light"] .auth-bg-glow-top {
          background: radial-gradient(ellipse, rgba(26, 95, 122, 0.2) 0%, transparent 70%);
          opacity: 0.2;
        }

        [data-theme="light"] .auth-bg-glow-bottom {
          background: radial-gradient(ellipse, rgba(26, 95, 122, 0.2) 0%, transparent 70%);
          opacity: 0.1;
        }

        .auth-container {
          width: 100%;
          padding: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
          z-index: 10;
        }

        .auth-card {
          width: 100%;
          border-radius: 16px;
        }

        .auth-card-content {
          padding: 40px;
        }

        .auth-logo {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-bottom: 24px;
        }

        .auth-logo-circle {
          width: 80px;
          height: 80px;
          padding: 12px;
          border-radius: 9999px;
          background: rgba(26, 29, 41, 0.5);
          border: 1px solid rgba(45, 51, 64, 1);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .auth-logo-circle img {
          width: 56px;
          height: 56px;
          object-fit: contain;
        }

        [data-theme="light"] .auth-logo-circle {
          background: rgba(255, 255, 255, 1);
          border: 1px solid rgba(203, 213, 224, 1);
        }

        .auth-icon {
          display: flex;
          justify-content: center;
          margin-bottom: 20px;
        }

        .auth-icon > * {
          width: 64px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          color: white;
        }

        /* Red variant (default) - for MFA verify, MFA setup */
        .auth-icon-default > *,
        .auth-icon-red > * {
          background: linear-gradient(135deg, #8B0000 0%, #A31621 100%);
          box-shadow: 0 0 30px rgba(139, 0, 0, 0.4);
        }

        /* Gold variant - for forgot password */
        .auth-icon-gold > * {
          background: linear-gradient(135deg, #B45309 0%, #D97706 100%);
          box-shadow: 0 0 30px rgba(217, 119, 6, 0.4);
        }

        /* Teal variant - for password reset form */
        .auth-icon-teal > * {
          background: linear-gradient(135deg, #0D9488 0%, #14B8A6 100%);
          box-shadow: 0 0 30px rgba(20, 184, 166, 0.4);
        }

        [data-theme="light"] .auth-icon-default > *,
        [data-theme="light"] .auth-icon-red > * {
          background: linear-gradient(135deg, #1A5F7A 0%, #3D96BD 100%);
          box-shadow: 0 0 20px rgba(26, 95, 122, 0.3);
        }

        [data-theme="light"] .auth-icon-gold > * {
          background: linear-gradient(135deg, #B45309 0%, #D97706 100%);
          box-shadow: 0 0 20px rgba(217, 119, 6, 0.3);
        }

        [data-theme="light"] .auth-icon-teal > * {
          background: linear-gradient(135deg, #0D9488 0%, #14B8A6 100%);
          box-shadow: 0 0 20px rgba(20, 184, 166, 0.3);
        }

        .auth-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .auth-title {
          font-family: var(--font-heading);
          font-size: var(--text-3xl);
          font-weight: 700;
          color: var(--color-text-primary);
          margin-bottom: 8px;
        }

        .auth-subtitle {
          font-size: var(--text-md);
          color: var(--color-text-secondary);
          margin: 0;
        }

        .auth-form {
          width: 100%;
        }

        .auth-footer {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
          gap: 2px;
          font-size: var(--text-xs);
          color: var(--color-text-muted);
        }

        .auth-footer-sep {
          margin: 0 6px;
          color: var(--color-text-muted);
        }

        .auth-footer-dot {
          margin: 0 4px;
          color: var(--color-text-muted);
        }

        .auth-footer-legal-link {
          color: var(--color-text-muted);
          text-decoration: none;
          font-size: var(--text-xs);
          transition: color 0.15s ease;
        }

        .auth-footer-legal-link:hover {
          color: var(--color-brand-primary);
        }

        /* Responsive */
        @media (max-width: 640px) {
          .auth-card-content {
            padding: 24px;
          }

          .auth-bg-glow-top {
            width: 400px;
            height: 200px;
            filter: blur(40px);
          }

          .auth-bg-glow-bottom {
            width: 300px;
            height: 150px;
            filter: blur(50px);
          }
        }
      `}</style>
    </div>
  );
}

export default AuthLayout;
